// Right-click context menu rendered at the mouse position. Closes on outside
// click or Escape. Each action is gated by the node kind (e.g. structural
// nodes don't get an "Add peer connection" entry because peer relationships
// are conceptually between deployable resources, but we still allow it — the
// spec doesn't restrict it).

import { useEffect, useRef } from "react";
import { Pencil, Trash2, Move, Plus, Link2, X } from "lucide-react";

const Item = ({ icon, label, onClick, testid, danger }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="motion-micro"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      background: "transparent",
      border: "none",
      color: danger ? "var(--color-danger)" : "var(--color-text-primary)",
      fontSize: 13,
      padding: "8px 12px",
      cursor: "pointer",
      textAlign: "left",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-background-tertiary)")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
  >
    {icon}
    {label}
  </button>
);

export default function NodeContextMenu({
  position,
  node,
  onClose,
  onAddChild,
  onAddPeer,
  onRename,
  onMove,
  onRemove,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!position) return;
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) onClose?.();
    };
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [position, onClose]);

  if (!position || !node) return null;

  // Keep the menu inside the viewport.
  const left = Math.min(position.x, window.innerWidth - 220);
  const top = Math.min(position.y, window.innerHeight - 240);

  return (
    <div
      ref={ref}
      data-testid="node-context-menu"
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 80,
        minWidth: 200,
        background: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 10,
        padding: "6px 0",
        boxShadow: "0 18px 40px -16px rgba(0,0,0,0.6)",
      }}
    >
      <div
        className="font-mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-tertiary)",
          padding: "6px 12px 4px",
        }}
      >
        {node.name}
      </div>
      <Item
        icon={<Plus size={14} />}
        label="Add child resource"
        onClick={onAddChild}
        testid="ctx-add-child"
      />
      <Item
        icon={<Link2 size={14} />}
        label="Add peer connection"
        onClick={onAddPeer}
        testid="ctx-add-peer"
      />
      <Item
        icon={<Pencil size={14} />}
        label="Rename"
        onClick={onRename}
        testid="ctx-rename"
      />
      <Item
        icon={<Move size={14} />}
        label="Move to…"
        onClick={onMove}
        testid="ctx-move"
      />
      <div
        style={{
          height: 1,
          background: "var(--color-border-primary)",
          margin: "4px 0",
        }}
      />
      <Item
        icon={<Trash2 size={14} />}
        label="Remove"
        onClick={onRemove}
        testid="ctx-remove"
        danger
      />
    </div>
  );
}

export { X };
